// Story 8.5 (ADO-2014), AC1 "review the cart", D-8: the cart cut-off banner (Figma
// "Cut-off banner" 5061:7519). Advisory only: it never blocks sending.
//
// Usage (mounted above the cart title on the review page):
//
//   const cutOff = useDepotCutOff(depotId);             // apps/web/src/basket
//   <CutOffBanner depotName={depotName} {...cutOff} />
//
// - One format for OPEN and PASSED (story D-14): "Order cut-off · {depot name}" /
//   "Deadline Wed 23 Sep, 2:00pm", with the Large pill (Small on phones, Figma Mobile 320;
//   story D-16). PASSED shows the next deadline, so once today's cut-off passes the banner
//   simply moves on to it.
// - `null` (loading, failed), NONE, and PASSED without a next deadline render NOTHING (AC4).
// Dates and times are the depot's (Europe/Dublin), never the browser's.

import { countdownDeadline, formatDeadline } from '../../shared/cutOffDisplay';
import {
  CutOffPill,
  type CutOffPillCountdown,
  type CutOffPillStatus,
} from '../CutOffPill/CutOffPill';
import styles from './CutOffBanner.module.css';

export interface CutOffBannerProps {
  readonly depotName: string | undefined;
  readonly status: CutOffPillStatus | null;
  readonly countdown: CutOffPillCountdown | null;
}

export const CUT_OFF_BANNER_TEST_ID = 'cut-off-banner';

const titled = (lead: string, depotName: string | undefined) =>
  depotName === undefined || depotName === '' ? lead : `${lead} · ${depotName}`;

export function CutOffBanner({ depotName, status, countdown }: CutOffBannerProps) {
  const deadline = countdownDeadline(status);
  if (status === null || deadline === null) return null;

  const title = titled('Order cut-off', depotName);

  return (
    <section
      className={styles.banner}
      aria-label={title}
      data-state={status.state}
      data-testid={CUT_OFF_BANNER_TEST_ID}
    >
      <div className={styles.copy}>
        <p className={styles.title}>{title}</p>
        <p className={styles.detail}>{`Deadline ${formatDeadline(deadline)}`}</p>
      </div>
      <CutOffPill status={status} countdown={countdown} size="large" phoneSize="small" />
    </section>
  );
}
